import React, { useState, useEffect } from "react";
import { Controlled as CodeMirror } from 'react-codemirror2';
import SweetAlert from "react-bootstrap-sweetalert";

// CodeMirror styles
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";

// @material-ui/icons
import { Error as ErrorIcon } from "components/MaterialSymbol/MaterialIcons";
import { Warning as WarningIcon } from "components/MaterialSymbol/MaterialIcons";

// core components
import Button from "components/CustomButtons/Button.js";
import Notification from "components/Notification/Notification.js";
import PageLayout from "layouts/PageLayout";


import DealerRequiredOverlay from "components/DealerRequiredOverlay/DealerRequiredOverlay";

// custom hooks & services
import useRobotsTxt from "hooks/useRobotsTxt";
import RobotsTxtService, { getProductionDefaultRobots } from "services/RobotsTxtService";
import useDealer from "hooks/useDealer";
import { validateRobotsTxt } from "utils/robotsValidation";

const useStyles = makeStyles((theme) => ({
    headerTitle: {
        margin: "0",
        fontWeight: "400",
        fontSize: "1.2rem",
    },
    editorWrapper: {
        position: "relative",
        marginTop: "10px",
    },
    editorContainer: {
        border: "1px solid #ddd",
        borderRadius: "4px",
        overflow: "hidden",
        "& .CodeMirror": {
            height: "400px",
            fontSize: "14px",
            fontFamily: "monospace",
        }
    },
    validationPanel: {
        marginTop: "20px",
        padding: "10px",
        border: "1px solid #eee",
        borderRadius: "4px",
        backgroundColor: "#fff",
    },
    errorItem: {
        color: "#f44336",
        paddingTop: "4px",
        paddingBottom: "4px",
    },
    warningItem: {
        color: "#ff9800",
        paddingTop: "4px",
        paddingBottom: "4px",
    },
    placeholderSection: {
        marginTop: "20px",
        padding: "15px",
        backgroundColor: "#f9f9f9",
        border: "1px dashed #ccc",
        borderRadius: "4px",
    },
    placeholderTitle: {
        fontSize: "14px",
        fontWeight: "bold",
        color: "#666",
        marginBottom: "10px",
    },
    placeholderContent: {
        fontSize: "13px",
        color: "#888",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
    },
    footerActions: {
        marginTop: "20px",
        display: "flex",
        gap: "10px",
    }
}));

export default function ManagedRobotsTxt() {
    const classes = useStyles();
    const dealerId = localStorage.getItem("_dlid");
    const hasDealer = !!dealerId;
    const dealerInfo = useDealer();
    
    const { robotsTxt, isLoading, mutate } = useRobotsTxt(dealerId);

    const [content, setContent] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [notification, setNotification] = useState(null);
    const [alert, setAlert] = useState(null);
    const [validation, setValidation] = useState({ errors: [], warnings: [] });

    const [isFirstLoad, setIsFirstLoad] = useState(true);

    useEffect(() => {
        // Only initialize when both API data and dealer context (domain) are definitively ready
        if (hasDealer && !isLoading && dealerInfo.id !== 0 && isFirstLoad) {
            const apiContent = robotsTxt?.trim();
            const domain = dealerInfo.dealerDomain;
            
            if (!domain) return; // Wait for domain resolution
            
            // Deterministic selection: Saved content OR Platform Baseline
            const finalContent = (apiContent && apiContent.length > 0)
                ? apiContent
                : getProductionDefaultRobots(domain);
            
            setContent(finalContent);
            setIsFirstLoad(false);
        }
    }, [robotsTxt, hasDealer, dealerInfo, isLoading, isFirstLoad]);



    useEffect(() => {
        setValidation(validateRobotsTxt(content, dealerInfo?.dealerDomain));
    }, [content, dealerInfo?.dealerDomain]);


    const performSave = async () => {
        setAlert(null);
        setIsSaving(true);
        try {
            await RobotsTxtService.update(dealerId, content);
            mutate();

            setNotification({ color: "success", message: "Robots.txt updated successfully!" });
        } catch (error) {
            console.error("Failed to save robots.txt:", error);
            setNotification({ color: "danger", message: "Failed to update robots.txt" });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = () => {
        if (!hasDealer || validation.errors.length > 0) return;

        if (validation.warnings.length > 0) {
            setAlert(
                <SweetAlert
                    warning
                    style={{ display: "block", marginTop: "-100px" }}
                    title="Validation Warnings"
                    onConfirm={performSave}
                    onCancel={() => setAlert(null)}
                    confirmBtnCssClass={
                        "btn btn-rose"
                    }
                    cancelBtnCssClass={
                        "btn btn-default"
                    }
                    confirmBtnText="Save Anyway"
                    cancelBtnText="Cancel"
                    showCancel
                >
                    <div style={{ textAlign: 'left', fontSize: '14px' }}>
                        <p>The following potential issues were detected:</p>
                        <ul>
                            {validation.warnings.map((w, i) => <li key={i}>{w.message}</li>)}
                        </ul>
                        <p>Are you sure you want to proceed?</p>
                    </div>
                </SweetAlert>
            );
        } else {
            performSave();
        }
    };

    const handleReset = () => {
        if (!hasDealer) return;
        const hostname = dealerInfo?.dealerDomain || "[your-domain.com]";
        setContent(getProductionDefaultRobots(hostname));
        setNotification({ color: "info", message: "Editor reset to production baseline." });
    };

    if (isLoading && hasDealer) {
        return (
            <PageLayout customComponent={<h4 className={classes.headerTitle}>Robots.txt Management</h4>}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
                    <CircularProgress />
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout 
            customComponent={<h4 className={classes.headerTitle}>Robots.txt Management</h4>}
        >
            {alert}
            {notification && (
                <Notification
                    open={!!notification}
                    color={notification.color}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}
            <Box className={classes.editorWrapper}>

                <DealerRequiredOverlay 
                    isVisible={!hasDealer}

                    message="Please select a dealer from the dropdown above to manage its robots.txt file."
                />
                
                <div className={classes.editorContainer}>
                    <CodeMirror
                        value={content}
                        options={{
                            mode: 'text/plain',
                            theme: 'material',
                            lineNumbers: true,
                            lineWrapping: true,
                            viewportMargin: Infinity,
                            readOnly: !hasDealer,
                        }}
                        onBeforeChange={(editor, data, value) => {
                            if (hasDealer) {
                                setContent(value);
                            }
                        }}
                    />
                </div>

                {hasDealer && (validation.errors.length > 0 || validation.warnings.length > 0) && (
                    <div className={classes.validationPanel}>
                        <Typography variant="subtitle2" gutterBottom>
                            Validation Results:
                        </Typography>
                        <List dense>
                            {validation.errors.map((err, i) => (
                                <ListItem key={i} className={classes.errorItem}>
                                    <ListItemIcon style={{ minWidth: '30px' }}><ErrorIcon fontSize="small" color="error" /></ListItemIcon>
                                    <ListItemText primary={`Line ${err.line}: ${err.message}`} />
                                </ListItem>
                            ))}
                            {validation.warnings.map((warn, i) => (
                                <ListItem key={i} className={classes.warningItem}>
                                    <ListItemIcon style={{ minWidth: '30px' }}><WarningIcon fontSize="small" style={{ color: '#ff9800' }} /></ListItemIcon>
                                    <ListItemText primary={warn.message} />
                                </ListItem>
                            ))}
                        </List>
                    </div>
                )}

                <div className={classes.placeholderSection}>
                    <div className={classes.placeholderTitle}>Standard Configuration</div>
                    <div className={classes.placeholderContent}>
                        {`These are the default SEO rules configured for: ${dealerInfo?.dealerDomain || 'your store'}.`}
                    </div>
                </div>



                <div className={classes.footerActions}>
                    <Button 
                        color="rose" 
                        onClick={handleSave}
                        disabled={isSaving || !hasDealer || validation.errors.length > 0}
                    >
                        {isSaving && <CircularProgress size={18} color="inherit" style={{ marginRight: '10px' }} />}
                        Save Changes
                    </Button>
                    <Button 
                        color="white" 
                        simple 
                        onClick={handleReset}
                        disabled={isSaving || !hasDealer}
                    >
                        Reset to Default
                    </Button>
                </div>
            </Box>



        </PageLayout>
    );
}
